import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from "@nexus/ui";
import { SetNameSchema, SET_NAME_MAX } from "@nexus/contracts";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { AdPlatformIcon } from "@/features/opportunities/components/AdPlatformIcon";
import {
  CreateSetDateRangePicker,
  PERSONA_EXTRA_ROLLING_PRESET,
} from "@/features/opportunities/components/sets/CreateSetDateRangePicker/CreateSetDateRangePicker";
import { MultiTagInputWithSelectAll } from "@/features/opportunities/components/sets/CreateSetDialog/MultiTagInputWithSelectAll";
import { humanizeBrandName } from "@/features/opportunities/lib/format-opportunity.js";
import { PersonaStatusBadge } from "@/features/personas/components/PersonaStatusBadge/PersonaStatusBadge";
import {
  humanizePersonaSentence,
  humanizePersonaTitle,
  personaInitials,
} from "@/features/personas/lib/persona-format";
import { personaStatusIsAwaitingReview } from "@/features/personas/lib/persona-status";
import type { Persona } from "@/features/personas/types/personas.types";
import {
  adPlatformsQueryOptions,
  competitorOptionsQueryOptions,
  industriesQueryOptions,
  personaOwnBrandQueryOptions,
  personasQueryOptions,
} from "@/lib/queries";
import { createDefaultPersonaOpportunityQueryTuning } from "../../../constants/persona-opportunity-tuning-defaults.js";
import {
  filterPersonasByIndustry,
  isPersonaSelectable,
} from "../../../lib/filter-personas-by-industry.js";
import {
  createDefaultCreatePersonaSetFormValues,
  createDefaultPersonaSetName,
  isFormComplete,
  missingRequirements,
  type CreatePersonaSetRequirement,
} from "../../../lib/persona-opportunity-form.js";
import type { CreatePersonaSetFormValues } from "../../../types/persona-opportunities.types.js";
import { PersonaQueryTuningFields } from "../PersonaQueryTuningFields/PersonaQueryTuningFields.js";

const PERSONA_OPPORTUNITIES_SCOPE = "persona-opportunities" as const;
/**
 * Full-strength `muted-foreground` clears AA at ~4.8:1; the previous `/55` blended to
 * roughly #adb1b9 on the light surface, about 2.1:1. These hints carry the industry
 * empty-state and the query-tuning explanation, which are the last things that should
 * fade out. 12px also matches the counter's warning and error states, so the character
 * count no longer resizes as it crosses the limit threshold.
 */
const FIELD_HINT_CLASS = "text-xs leading-snug text-muted-foreground";

const REQUIREMENT_LABEL_KEYS: Record<CreatePersonaSetRequirement, string> = {
  name: "opportunities.setNameLabel",
  industry: "personaOpportunities.createDialog.industryLabel",
  competitors: "personaOpportunities.createDialog.competitorsLabel",
  adPlatform: "opportunities.adPlatformsLabel",
  personas: "personaOpportunities.createDialog.personasLabel",
  dataWindow: "personaOpportunities.createDialog.dateRangeLabel",
  advanced: "personaOpportunities.createDialog.advancedLabel",
};

/** Every field `isFormComplete` gates on carries this, not just the name. */
function RequiredMark() {
  return (
    <span className="text-destructive text-sm" aria-hidden>
      *
    </span>
  );
}

/**
 * Approved personas lead each group: they are the vetted picks, and the list already
 * excludes REJECTED, so SUGGESTED is the only status left that still owes a review.
 */
function sortApprovedFirst(personas: readonly Persona[]): Persona[] {
  return [...personas].sort(
    (a, b) =>
      Number(personaStatusIsAwaitingReview(a.status)) -
      Number(personaStatusIsAwaitingReview(b.status)),
  );
}

function toPersonaTagOption(persona: Persona, group: string) {
  const displayTitle = humanizePersonaTitle(persona.title);
  const who = persona.who?.trim();
  const whoLine = who ? humanizePersonaSentence(who) : undefined;
  const motivation = persona.motivations[0]?.trim();
  // What the persona wants separates two rows faster than who they are: `who` opens with
  // shared demographic scaffolding and truncates before it gets specific, while the
  // motivation is short and already distinct. Same `motivations[0]` the persona cards show.
  const driverLine = motivation ? humanizePersonaSentence(motivation) : undefined;
  return {
    id: persona.id,
    label: displayTitle,
    value: persona.id,
    // Approved is the norm here and stays unlabelled, which leaves the whole line for
    // the persona itself; only the unreviewed rows spend a chip.
    description: driverLine ?? whoLine,
    // Full `who` survives on hover; it only leaves the row because it cannot finish there.
    detail: driverLine ? whoLine : undefined,
    badge: personaStatusIsAwaitingReview(persona.status) ? (
      <PersonaStatusBadge status={persona.status} />
    ) : undefined,
    imageUrl: persona.avatarUrl ?? undefined,
    imageFallback: personaInitials(displayTitle),
    group,
  };
}

function validateSetName(raw: string) {
  const result = SetNameSchema.safeParse(raw);
  if (result.success) return { value: result.data, error: null };
  return { value: raw.trim(), error: result.error.issues[0]?.message ?? "Invalid name" };
}

function CollapsibleSection({
  open,
  onToggle,
  label,
  hint,
  children,
}: {
  open: boolean;
  onToggle: () => void;
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-border">
      <button
        type="button"
        data-testid="persona-advanced-options"
        onClick={onToggle}
        className={cn(
          "flex w-full items-center gap-3 px-4 py-3 text-left transition-colors rounded-lg",
          "hover:bg-accent/50 focus-visible:outline-none focus-visible:bg-accent/50",
          open && "rounded-b-none border-b border-border",
        )}
        aria-expanded={open}
      >
        <Icon name="settings" size="sm" color="muted" className="shrink-0 self-center" />
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <p className="text-sm font-semibold leading-tight text-foreground">{label}</p>
          <p className="text-xs leading-snug text-muted-foreground">{hint}</p>
        </div>
        <Icon
          name="chevron-down"
          size="xs"
          color="muted"
          className={cn("shrink-0 transition-transform duration-200", open && "rotate-180")}
        />
      </button>
      <div
        className={cn(
          "grid transition-all duration-200 ease-out",
          open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0",
        )}
      >
        <div className="overflow-hidden">
          <div className="flex flex-col gap-4 p-4">{children}</div>
        </div>
      </div>
    </div>
  );
}

export interface CreatePersonaSetDialogProps {
  tenantId: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onSubmit?: (values: CreatePersonaSetFormValues) => void;
  isSubmitting?: boolean;
  initialName?: string;
  canUseQueryTuning?: boolean;
  /** Persona this dialog was opened for (the library CTA) — preselected on open. */
  initialPersonaId?: string;
}

export function CreatePersonaSetDialog({
  tenantId,
  open,
  onOpenChange,
  onSubmit,
  isSubmitting,
  initialName,
  canUseQueryTuning = false,
  initialPersonaId,
}: CreatePersonaSetDialogProps) {
  const { t } = useTranslation();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const didInitForOpenRef = React.useRef(false);
  const autoSelectedCompetitorsForRef = React.useRef<string | null>(null);
  const seededPersonaIdRef = React.useRef<string | null>(null);

  const [form, setForm] = React.useState<CreatePersonaSetFormValues>(() =>
    createDefaultCreatePersonaSetFormValues(),
  );
  const [touched, setTouched] = React.useState(false);
  const [advancedOpen, setAdvancedOpen] = React.useState(false);

  const catalogEnabled = !!open && !!tenantId;

  const {
    data: industries = [],
    isLoading: industriesLoading,
    isError: industriesIsError,
    isSuccess: industriesIsSuccess,
    refetch: refetchIndustries,
  } = useQuery({
    ...industriesQueryOptions(PERSONA_OPPORTUNITIES_SCOPE, tenantId),
    enabled: catalogEnabled,
  });

  const { data: competitorRows = [], isLoading: competitorsLoading } = useQuery({
    ...competitorOptionsQueryOptions(PERSONA_OPPORTUNITIES_SCOPE, tenantId, form.industry),
    enabled: catalogEnabled && !!form.industry,
  });

  const {
    data: ownBrand,
    isSuccess: ownBrandReady,
    isError: ownBrandIsError,
  } = useQuery({
    ...personaOwnBrandQueryOptions(tenantId),
    enabled: catalogEnabled,
  });

  const ownBrandSlug = ownBrand?.slug ?? null;
  const ownBrandDisplayName = ownBrandSlug ? humanizeBrandName(ownBrandSlug) : null;
  const ownBrandMissing = ownBrandIsError || (ownBrandReady && !ownBrandSlug);
  const ownBrandConfigured = ownBrandReady && Boolean(ownBrandSlug);

  const industriesEmpty =
    industriesIsSuccess && !industriesLoading && industries.length === 0;
  const industriesUnavailable =
    industriesLoading || industriesIsError || industriesEmpty || ownBrandMissing;

  const { data: personasPage, isLoading: personasLoading } = useQuery({
    ...personasQueryOptions(tenantId),
    enabled: catalogEnabled,
  });

  const { data: adPlatforms = [], isLoading: adPlatformsLoading } = useQuery({
    ...adPlatformsQueryOptions(tenantId),
    enabled: catalogEnabled,
  });

  const competitorOptions = React.useMemo(
    () =>
      competitorRows
        .filter((option) => !ownBrandSlug || option.name !== ownBrandSlug)
        .map((option) => ({
          id: option.name,
          // Raw `name` stays the submitted value; label is display-only.
          label: humanizeBrandName(option.name),
          value: option.name,
        })),
    [competitorRows, ownBrandSlug],
  );

  // A stale link — to a persona that was rejected, removed, or belongs to another
  // tenant — seeds nothing rather than putting a row in the form the picker itself
  // would not offer.
  const initialPersona = React.useMemo(() => {
    if (!initialPersonaId) return null;
    const persona = (personasPage?.personas ?? []).find(
      (candidate) => candidate.id === initialPersonaId,
    );
    return persona && isPersonaSelectable(persona) ? persona : null;
  }, [personasPage?.personas, initialPersonaId]);

  // Only industries where this organization is a client reach the select, and a
  // persona's own industry is not guaranteed to be one of them.
  const initialIndustry =
    initialPersona?.industry &&
    industries.some((option) => option.index === initialPersona.industry)
      ? initialPersona.industry
      : null;

  const personaOptions = React.useMemo(() => {
    const personas = filterPersonasByIndustry(personasPage?.personas ?? [], form.industry);
    const core = sortApprovedFirst(personas.filter((persona) => persona.type === "CORE_SUGGESTED"));
    const industry = sortApprovedFirst(
      personas.filter((persona) => persona.type === "INDUSTRY_SUGGESTED"),
    );
    const coreGroup = t("personaOpportunities.createDialog.personasGroupCore");
    const industryGroup = t("personaOpportunities.createDialog.personasGroupIndustry");

    return [
      ...core.map((persona) => toPersonaTagOption(persona, coreGroup)),
      ...industry.map((persona) => toPersonaTagOption(persona, industryGroup)),
    ];
  }, [personasPage?.personas, form.industry, t]);

  React.useEffect(() => {
    if (!open) {
      didInitForOpenRef.current = false;
      autoSelectedCompetitorsForRef.current = null;
      seededPersonaIdRef.current = null;
      setTouched(false);
      setAdvancedOpen(false);
      return;
    }
    if (didInitForOpenRef.current) return;
    didInitForOpenRef.current = true;

    setForm({
      ...createDefaultCreatePersonaSetFormValues(),
      name: initialName ?? createDefaultPersonaSetName(),
      advanced: createDefaultPersonaOpportunityQueryTuning(),
    });
    requestAnimationFrame(() => inputRef.current?.select());
  }, [open, initialName]);

  // Opened from a persona: land on a form that already has that persona picked, and
  // its industry with it. Waits for the industry list — seeding first would drop the
  // industry, and re-running once it arrived would overwrite a manual choice.
  React.useEffect(() => {
    if (!open || !initialPersona || !industriesIsSuccess) return;
    if (seededPersonaIdRef.current === initialPersona.id) return;
    seededPersonaIdRef.current = initialPersona.id;
    // Without an eligible industry there is nothing to select the persona *within*;
    // `handleIndustryChange` picks it up as soon as the user chooses one.
    if (!initialIndustry) return;
    autoSelectedCompetitorsForRef.current = null;
    setForm((previous) => ({
      ...previous,
      industry: initialIndustry,
      industryCompetitors: [],
      personaIds: [initialPersona.id],
    }));
  }, [open, initialPersona, initialIndustry, industriesIsSuccess]);

  React.useEffect(() => {
    if (!open || adPlatforms.length === 0 || form.adPlatform) return;
    setForm((previous) => ({ ...previous, adPlatform: adPlatforms[0]?.id ?? "" }));
  }, [open, adPlatforms, form.adPlatform]);

  // First load for an industry pre-selects every competitor option (same as the
  // opportunities create dialog). Wait for a successful own-brand response so a
  // failed lookup cannot lock the client brand into the selection. Manual edits
  // after that are left alone.
  React.useEffect(() => {
    if (!open || !form.industry || competitorsLoading || !ownBrandReady) return;
    if (autoSelectedCompetitorsForRef.current === form.industry) return;
    autoSelectedCompetitorsForRef.current = form.industry;
    setForm((previous) => ({
      ...previous,
      industryCompetitors: competitorOptions.map((option) => option.value),
    }));
  }, [open, form.industry, competitorsLoading, competitorOptions, ownBrandReady]);

  // Drop the own brand if it was selected before the slug resolved (race / industry change).
  React.useEffect(() => {
    if (!ownBrandSlug) return;
    setForm((previous) => {
      if (!previous.industryCompetitors.includes(ownBrandSlug)) return previous;
      return {
        ...previous,
        industryCompetitors: previous.industryCompetitors.filter(
          (competitor) => competitor !== ownBrandSlug,
        ),
      };
    });
  }, [ownBrandSlug]);

  const { value: trimmedName, error: nameError } = validateSetName(form.name);
  const showNameError = touched && !!nameError;
  const charCount = trimmedName.length;
  const nearLimit = charCount >= SET_NAME_MAX * 0.9;
  const missing = missingRequirements({ ...form, name: trimmedName });
  const canGenerate = missing.length === 0 && ownBrandConfigured;
  // Reuses each field's own label so the summary names the control to scroll back to
  // rather than inventing a second vocabulary for the same fields.
  const missingFieldLabels = missing.map((requirement) =>
    t(REQUIREMENT_LABEL_KEYS[requirement]),
  );

  function updateForm(patch: Partial<CreatePersonaSetFormValues>) {
    setForm((previous) => ({ ...previous, ...patch }));
  }

  function handleIndustryChange(industry: string) {
    autoSelectedCompetitorsForRef.current = null;
    // The persona the dialog was opened for survives an industry change wherever the
    // new industry still offers it — a core persona is offered under every industry,
    // so switching industries must not silently drop the reason this dialog is open.
    const keptPersonaIds =
      initialPersona && filterPersonasByIndustry([initialPersona], industry).length > 0
        ? [initialPersona.id]
        : [];
    updateForm({
      industry,
      industryCompetitors: [],
      personaIds: keptPersonaIds,
    });
  }

  function handleGenerate() {
    setTouched(true);
    const nextForm = { ...form, name: trimmedName };
    if (!isFormComplete(nextForm) || !ownBrandConfigured) return;
    onSubmit?.(nextForm);
  }

  return (
    <Dialog open={open} onOpenChange={(next) => { if (isSubmitting) return; onOpenChange?.(next); }}>
      <DialogContent
        variant="form"
        className="flex max-h-[min(90vh,760px)] flex-col"
        onInteractOutside={(event) => event.preventDefault()}
        onEscapeKeyDown={(event) => event.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{t("personaOpportunities.createDialog.title")}</DialogTitle>
          <DialogDescription>{t("personaOpportunities.createDialog.description")}</DialogDescription>
        </DialogHeader>

        <form
          className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto py-1 pr-1"
          onSubmit={(event) => {
            event.preventDefault();
            handleGenerate();
          }}
        >
          {ownBrandDisplayName ? (
            <div
              className="rounded-md border border-border bg-muted/50 px-3 py-2.5"
              data-testid="persona-set-client-brand"
            >
              <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {t("personaOpportunities.createDialog.clientBrandLabel", "Client brand")}
              </p>
              <p className="mt-0.5 text-base font-medium text-foreground">{ownBrandDisplayName}</p>
            </div>
          ) : ownBrandMissing ? (
            <p role="alert" className="text-xs text-destructive">
              {t(
                "personaOpportunities.createDialog.clientBrandRequired",
                "The client brand needs to be configured.",
              )}
            </p>
          ) : null}

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-0.5">
                <Label htmlFor="persona-set-name">{t("opportunities.setNameLabel")}</Label>
                <RequiredMark />
              </div>
              <span
                className={cn(
                  "shrink-0 tabular-nums transition-colors duration-150",
                  showNameError
                    ? "text-xs text-destructive"
                    : nearLimit
                      ? "text-xs text-status-warning"
                      : FIELD_HINT_CLASS,
                )}
              >
                {charCount}/{SET_NAME_MAX}
              </span>
            </div>
            <Input
              ref={inputRef}
              id="persona-set-name"
              placeholder={t("opportunities.setNamePlaceholder")}
              value={form.name}
              onChange={(event) => {
                updateForm({ name: event.target.value });
                if (!touched) setTouched(true);
              }}
              onBlur={() => setTouched(true)}
              maxLength={SET_NAME_MAX}
              aria-invalid={showNameError || undefined}
              aria-describedby={showNameError ? "persona-set-name-error" : undefined}
            />
            {showNameError ? (
              <p id="persona-set-name-error" role="alert" className="text-xs text-destructive">
                {nameError}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <div className="flex items-center gap-0.5">
                <Label htmlFor="persona-set-industry">
                  {t("personaOpportunities.createDialog.industryLabel")}
                </Label>
                <RequiredMark />
              </div>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="inline-flex min-h-11 min-w-11 shrink-0 items-center justify-center rounded-sm text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring [@media(hover:hover)_and_(pointer:fine)]:min-h-7 [@media(hover:hover)_and_(pointer:fine)]:min-w-7"
                      aria-label={t(
                        "personaOpportunities.createDialog.industryHelpAria",
                        "About client industries",
                      )}
                    >
                      <Icon name="info" size="xs" color="muted" aria-hidden />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-56">
                    {t(
                      "personaOpportunities.createDialog.industryHelp",
                      "Only industries where this organization is a client. Assigned industries without the client are hidden.",
                    )}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Select
              value={form.industry}
              onValueChange={handleIndustryChange}
              disabled={industriesUnavailable}
            >
              <SelectTrigger id="persona-set-industry" aria-busy={industriesLoading || undefined}>
                {industriesLoading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="loader" size="xs" className="animate-spin" />
                    {t("common.loading")}
                  </span>
                ) : (
                  <SelectValue placeholder={t("personaOpportunities.createDialog.industryPlaceholder")} />
                )}
              </SelectTrigger>
              <SelectContent>
                {industries.map((option) => (
                  <SelectItem key={option.index} value={option.index}>
                    {option.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {industriesIsError ? (
              <div className="flex flex-col gap-1.5" role="alert">
                <p className="text-xs text-destructive">
                  {t(
                    "personaOpportunities.createDialog.industryLoadError",
                    "Couldn’t load eligible industries. Try again.",
                  )}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="w-fit"
                  onClick={() => {
                    void refetchIndustries();
                  }}
                >
                  {t("common.retry", "Retry")}
                </Button>
              </div>
            ) : null}
            {industriesEmpty ? (
              <p className={FIELD_HINT_CLASS} role="status">
                {t(
                  "personaOpportunities.createDialog.industryEmptyEligible",
                  "No eligible industries for this organization. The client must appear in an industry before you can create a set.",
                )}
              </p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="persona-set-competitors">{t("personaOpportunities.createDialog.competitorsLabel")}</Label>
              <RequiredMark />
            </div>
            <MultiTagInputWithSelectAll
              id="persona-set-competitors"
              options={competitorOptions}
              value={form.industryCompetitors}
              onChange={(industryCompetitors) => updateForm({ industryCompetitors })}
              disabled={!form.industry || competitorsLoading}
              disableFreeText
              renderTag={humanizeBrandName}
              placeholder={t("personaOpportunities.createDialog.competitorsPlaceholder")}
              noOptionsText={t("personaOpportunities.createDialog.competitorsNoOptions")}
              selectedOfTotalLabel={(selected, total) =>
                t("personaOpportunities.createDialog.competitorsSelectedOfTotal", { selected, total })}
              noMatchText={(query) => t("personaOpportunities.createDialog.competitorsNoMatch", { query })}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona-set-platform">{t("opportunities.adPlatformsLabel")}</Label>
            <Select
              value={form.adPlatform}
              onValueChange={(adPlatform) => updateForm({ adPlatform })}
              disabled={adPlatformsLoading}
            >
              <SelectTrigger id="persona-set-platform" aria-busy={adPlatformsLoading || undefined}>
                {adPlatformsLoading ? (
                  <span className="flex items-center gap-2 text-muted-foreground">
                    <Icon name="loader" size="xs" className="animate-spin" />
                    {t("common.loading")}
                  </span>
                ) : (
                  <SelectValue placeholder={t("opportunities.adPlatformPlaceholder")} />
                )}
              </SelectTrigger>
              <SelectContent>
                {adPlatforms.map((platform) => (
                  <SelectItem key={platform.id} value={platform.id}>
                    {/* `inline-flex` rather than `flex`: Select mirrors this into the
                        trigger's `truncate` span, which only ellipsizes inline content. */}
                    <span className="inline-flex items-center gap-2 align-middle">
                      <AdPlatformIcon platformId={platform.id} />
                      {platform.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="persona-set-personas">{t("personaOpportunities.createDialog.personasLabel")}</Label>
              <RequiredMark />
            </div>
            <MultiTagInputWithSelectAll
              id="persona-set-personas"
              options={personaOptions}
              value={form.personaIds}
              onChange={(personaIds) => updateForm({ personaIds })}
              disabled={!form.industry || personasLoading}
              disableFreeText
              placeholder={t("personaOpportunities.createDialog.personasPlaceholder")}
              noOptionsText={t("personaOpportunities.createDialog.personasNoOptions")}
              selectedOfTotalLabel={(selected, total) =>
                t("personaOpportunities.createDialog.personasSelectedOfTotal", { selected, total })}
              noMatchText={(query) => t("personaOpportunities.createDialog.personasNoMatch", { query })}
            />
            {!form.industry ? (
              <p className={FIELD_HINT_CLASS}>{t("personaOpportunities.createDialog.personasIndustryHint")}</p>
            ) : null}
          </div>

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-0.5">
              <Label htmlFor="persona-set-date-range">{t("personaOpportunities.createDialog.dateRangeLabel", "Date range")}</Label>
              <RequiredMark />
            </div>
            <CreateSetDateRangePicker
              id="persona-set-date-range"
              startDate={form.startDate}
              endDate={form.endDate}
              activePresetId={form.activePresetId}
              extraRollingPresets={[PERSONA_EXTRA_ROLLING_PRESET]}
              onChange={(next) => updateForm(next)}
              disabled={isSubmitting}
            />
          </div>

          {canUseQueryTuning ? (
            <CollapsibleSection
              open={advancedOpen}
              onToggle={() => {
                const nextOpen = !advancedOpen;
                setAdvancedOpen(nextOpen);
                updateForm({ showAdvanced: nextOpen });
              }}
              label={t("personaOpportunities.createDialog.advancedLabel")}
              hint={t("personaOpportunities.createDialog.advancedHint")}
            >
              <PersonaQueryTuningFields
                value={form.advanced}
                onChange={(advanced) => updateForm({ advanced })}
                disabled={isSubmitting}
              />
            </CollapsibleSection>
          ) : null}
        </form>

        <DialogFooter>
          {/* A greyed-out Generate is otherwise silent about why. `status` announces the
              remaining fields as they resolve, which a `disabled` button cannot do —
              it is skipped by assistive tech and never receives focus. */}
          {missingFieldLabels.length > 0 ? (
            <p
              role="status"
              data-testid="persona-set-missing"
              className="text-xs text-muted-foreground sm:mr-auto sm:self-center"
            >
              {t("personaOpportunities.createDialog.missingRequirements", {
                fields: missingFieldLabels.join(", "),
              })}
            </p>
          ) : null}
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange?.(false)}
            disabled={isSubmitting}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            data-testid="persona-set-generate"
            onClick={handleGenerate}
            disabled={!canGenerate || isSubmitting}
          >
            {isSubmitting ? t("personaOpportunities.createDialog.generating") : t("personaOpportunities.createDialog.generate")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
